import { useMemo } from 'react';
import { BUSINESS_TIMEZONE } from '../utils/date';
import { splitBilingual } from '../utils/bilingual';
import {
  normalizeScheduleGrid,
  formatTime,
  type ScheduleEvent,
  type ScheduleGridData,
} from '../types/schedule';

interface Props {
  data: ScheduleGridData;
  /** Highlight the column for the current weekday (business TZ). Default true. */
  highlightToday?: boolean;
}

/** Vertical pixels per hour on the time axis. Taller rows keep 30-min blocks
 *  legible (time + English + Chinese lines) without clipping. */
const HOUR_HEIGHT = 96;
const PX_PER_MIN = HOUR_HEIGHT / 60;
/** Minimum rendered height for a timed event block, for legibility. */
const MIN_EVENT_HEIGHT = 28;
/** Height of one untimed ("all-day") event block in the top band. */
const UNTIMED_HEIGHT = 34;
/** Axis fallback when a schedule has no timed events: 8:00 – 18:00. */
const FALLBACK_MIN = 8 * 60;
const FALLBACK_MAX = 18 * 60;

/** Light block palettes; each event gets one deterministically (by activity),
 *  so the same activity reads consistently and adjacent blocks contrast. */
const PALETTE = [
  { bg: 'bg-blue-50', border: 'border-blue-200', time: 'text-blue-700' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', time: 'text-emerald-700' },
  { bg: 'bg-amber-50', border: 'border-amber-200', time: 'text-amber-700' },
  { bg: 'bg-rose-50', border: 'border-rose-200', time: 'text-rose-700' },
  { bg: 'bg-violet-50', border: 'border-violet-200', time: 'text-violet-700' },
  { bg: 'bg-cyan-50', border: 'border-cyan-200', time: 'text-cyan-700' },
  { bg: 'bg-orange-50', border: 'border-orange-200', time: 'text-orange-700' },
  { bg: 'bg-teal-50', border: 'border-teal-200', time: 'text-teal-700' },
  { bg: 'bg-pink-50', border: 'border-pink-200', time: 'text-pink-700' },
  { bg: 'bg-indigo-50', border: 'border-indigo-200', time: 'text-indigo-700' },
];

/** Deterministic light color for an activity string. */
function colorFor(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (Math.imul(h, 31) + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/** English weekday name in the business timezone, e.g. "Monday". */
function todayWeekdayName(): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    weekday: 'long',
  }).format(new Date());
}

/** "HH:MM" → minutes since midnight, or null when blank/unparseable. */
function toMinutes(hhmm: string): number | null {
  const m = String(hhmm ?? '').match(/^(\d{2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/** Axis tick label for a whole-hour minute offset, e.g. 480 → "8 AM", 780 → "1 PM". */
function hourLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${ampm}`;
}

interface PlacedEvent {
  event: ScheduleEvent;
  top: number;
  height: number;
  leftPct: number;
  widthPct: number;
}

/**
 * Lay timed events out within a single day column: position by start/end and
 * split width across overlapping events (greedy lane packing per collision
 * cluster). `axisMin` anchors the top of the column.
 */
function layoutTimed(events: { event: ScheduleEvent; start: number; end: number }[], axisMin: number): PlacedEvent[] {
  const sorted = [...events].sort((a, b) => a.start - b.start || a.end - b.end);
  const placed: PlacedEvent[] = [];
  let i = 0;
  while (i < sorted.length) {
    // Build a collision cluster: events that transitively overlap.
    const cluster = [sorted[i]];
    let clusterEnd = sorted[i].end;
    let j = i + 1;
    while (j < sorted.length && sorted[j].start < clusterEnd) {
      cluster.push(sorted[j]);
      clusterEnd = Math.max(clusterEnd, sorted[j].end);
      j++;
    }
    // Assign each cluster event to the first free lane.
    const laneEnds: number[] = [];
    const laneOf = new Map<number, number>();
    cluster.forEach((c, idx) => {
      let lane = laneEnds.findIndex(end => end <= c.start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(c.end);
      } else {
        laneEnds[lane] = c.end;
      }
      laneOf.set(idx, lane);
    });
    const lanes = laneEnds.length;
    cluster.forEach((c, idx) => {
      const lane = laneOf.get(idx) ?? 0;
      const height = Math.max(MIN_EVENT_HEIGHT, (c.end - c.start) * PX_PER_MIN);
      placed.push({
        event: c.event,
        top: (c.start - axisMin) * PX_PER_MIN,
        height,
        leftPct: (lane / lanes) * 100,
        widthPct: (1 / lanes) * 100,
      });
    });
    i = j;
  }
  return placed;
}

function EventBlock({ activity }: { activity: string }) {
  const { en, zh } = splitBilingual(activity);
  return (
    <>
      <span className="block leading-tight text-gray-800">{en}</span>
      {zh && <span className="block text-[10px] text-gray-500 leading-tight">{zh}</span>}
    </>
  );
}

/**
 * Read-only week-calendar renderer for a program's daily activity timetable.
 * Each day owns its own timed events; events are positioned on a shared time
 * axis so days can start/end at different times. Untimed events render in an
 * "all-day" band at the top. Horizontally scrollable with a sticky time gutter;
 * highlights today's weekday column. Shared by the Teacher Portal and the admin
 * editor preview.
 */
export function ScheduleGrid({ data, highlightToday = true }: Props) {
  const grid = useMemo(() => normalizeScheduleGrid(data), [data]);

  const todayCol = useMemo(() => {
    if (!highlightToday) return -1;
    const today = todayWeekdayName();
    return grid.days.findIndex(d => d.label.startsWith(today));
  }, [grid.days, highlightToday]);

  const layout = useMemo(() => {
    // Split each day into timed + untimed events.
    const perDay = grid.days.map(day => {
      const timed: { event: ScheduleEvent; start: number; end: number }[] = [];
      const untimed: ScheduleEvent[] = [];
      for (const event of day.events) {
        const start = toMinutes(event.start);
        const end = toMinutes(event.end);
        if (start == null) untimed.push(event);
        else timed.push({ event, start, end: end == null || end < start ? start : end });
      }
      return { label: day.label, timed, untimed };
    });

    const starts = perDay.flatMap(d => d.timed.map(t => t.start));
    const ends = perDay.flatMap(d => d.timed.map(t => t.end));
    const hasTimed = starts.length > 0;
    const axisMin = hasTimed ? Math.floor(Math.min(...starts) / 60) * 60 : FALLBACK_MIN;
    const axisMax = hasTimed ? Math.ceil(Math.max(...ends) / 60) * 60 : FALLBACK_MAX;
    const allDayCount = Math.max(0, ...perDay.map(d => d.untimed.length));
    const bandHeight = allDayCount * UNTIMED_HEIGHT;
    const timedHeight = (axisMax - axisMin) * PX_PER_MIN;

    const hours: number[] = [];
    for (let m = axisMin; m <= axisMax; m += 60) hours.push(m);

    const columns = perDay.map(d => ({
      label: d.label,
      untimed: d.untimed,
      placed: layoutTimed(d.timed, axisMin),
    }));

    return { columns, axisMin, bandHeight, timedHeight, hours };
  }, [grid.days]);

  const hasAnyEvent = grid.days.some(d => d.events.length > 0);
  if (grid.days.length === 0 || !hasAnyEvent) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <p className="text-gray-500 text-sm">No schedule has been set yet.</p>
      </div>
    );
  }

  const { columns, axisMin, bandHeight, timedHeight, hours } = layout;
  const totalHeight = bandHeight + timedHeight;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <div className="flex min-w-max">
        {/* Time gutter */}
        <div className="sticky left-0 z-10 bg-white border-r border-gray-200 flex-shrink-0 w-16">
          <div className="h-9 border-b border-gray-200 bg-gray-50 px-2 flex items-end pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Time
          </div>
          <div className="relative" style={{ height: totalHeight }}>
            {hours.map(m => (
              <div
                key={m}
                className="absolute right-2 -translate-y-1/2 text-[11px] text-gray-400 whitespace-nowrap"
                style={{ top: bandHeight + (m - axisMin) * PX_PER_MIN }}
              >
                {hourLabel(m)}
              </div>
            ))}
          </div>
        </div>

        {/* Day columns */}
        {columns.map((col, c) => {
          const { en, zh } = splitBilingual(col.label);
          const isToday = c === todayCol;
          return (
            <div
              key={c}
              className={`flex-1 min-w-[9rem] border-l border-gray-200 ${isToday ? 'bg-blue-50/40' : ''}`}
            >
              {/* Header */}
              <div
                className={`h-9 border-b border-gray-200 px-2 flex flex-col items-center justify-center text-center ${
                  isToday ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-500'
                }`}
              >
                <span className="block text-xs font-semibold uppercase tracking-wide leading-none">{en}</span>
                {zh && <span className="block font-normal normal-case text-[10px] leading-none mt-0.5">{zh}</span>}
              </div>

              {/* Column body */}
              <div className="relative" style={{ height: totalHeight }}>
                {/* All-day band */}
                {col.untimed.map((event, i) => {
                  const clr = colorFor(event.activity);
                  return (
                    <div
                      key={`u${i}`}
                      title={event.activity}
                      className={`absolute inset-x-1 rounded-md border ${clr.border} ${clr.bg} px-1.5 py-0.5 text-[12px] text-gray-800 overflow-hidden`}
                      style={{ top: i * UNTIMED_HEIGHT, height: UNTIMED_HEIGHT - 2 }}
                    >
                      <EventBlock activity={event.activity} />
                    </div>
                  );
                })}

                {/* Hour gridlines */}
                {hours.map(m => (
                  <div
                    key={m}
                    className="absolute inset-x-0 border-t border-gray-100"
                    style={{ top: bandHeight + (m - axisMin) * PX_PER_MIN }}
                  />
                ))}

                {/* Timed events: from–to time on top, then activity (EN over a
                    smaller Chinese line). Each block gets a light color by activity. */}
                {col.placed.map((p, i) => {
                  const clr = colorFor(p.event.activity);
                  const timeLabel = p.event.end && p.event.end !== p.event.start
                    ? `${formatTime(p.event.start)} – ${formatTime(p.event.end)}`
                    : formatTime(p.event.start);
                  return (
                    <div
                      key={i}
                      title={`${timeLabel}  ${p.event.activity}`}
                      className={`absolute rounded-md border ${clr.border} ${clr.bg} px-1.5 py-1 text-[12px] text-gray-800 overflow-hidden`}
                      style={{
                        top: bandHeight + p.top,
                        height: p.height - 2,
                        left: `${p.leftPct}%`,
                        width: `calc(${p.widthPct}% - 4px)`,
                        marginLeft: 2,
                      }}
                    >
                      <span className={`block text-[10px] font-semibold ${clr.time} leading-none mb-0.5`}>
                        {timeLabel}
                      </span>
                      <EventBlock activity={p.event.activity} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
